import axios from "axios";
import { ArcElement, Chart, Legend, Tooltip } from "chart.js";
import { format } from "date-fns";
import {
  BarChart2,
  Eraser,
  Filter,
  RefreshCwOff,
  Save,
  Search,
  SearchCheck,
  Trash,
  X,
} from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  fetchDepartamentos,
  fetchSecciones,
  fetchTiposDocumento,
} from "../services/api";
import {
  ApiFiltrosDocumento,
  Departamento,
  FiltrosDocumento,
  Seccion,
  TipoDocumento,
} from "../types";
import { DatePicker } from "./DatePicker";
import { DatePickerWithRange } from "./DatePickerWithRange";
import { Button } from "./ui/button";
import { Modal } from "./ui/filters_modal";
import { Input } from "./ui/input";
import { MultiSelect } from "./ui/multi-select";

import PremiumButton from "./PremiumButton";
import PremiumDialog from "./PremiumDialog";

// Register the necessary components
Chart.register(ArcElement, Tooltip, Legend);

interface FiltersProps {
  onApplyFilters: (filtros: ApiFiltrosDocumento) => void;
}

interface SavedFilter {
  id: number; // o string, dependiendo de cómo se maneje en tu base de datos
  filter_name: string;
  section_filter?: string;
  department_filter?: string;
  document_type_filter?: string;
}

// Define the types for the statistics
interface SimpleStatistic {
  tipo_documento: string;
  cantidad: number;
  porcentaje: number;
}

interface DetailedStatistic {
  departamento_nombre: string;
  tipo_documento: string;
  cantidad: number;
  porcentaje: number;
}

interface Stats {
  total_count: number;
  simple: SimpleStatistic[];
  detailed: DetailedStatistic[];
  // Campos adicionales para manejar respuestas vacías
  fecha?: string;
  total_documentos?: number;
  secciones?: any[];
  departamentos?: any[];
  tipos_documento?: any[];
}

const Filters: React.FC<FiltersProps> = ({ onApplyFilters }) => {
  const [filtros, setFiltros] = useState<FiltrosDocumento>({});
  const [secciones, setSecciones] = useState<Seccion[]>([]);
  const [departamentos, setDepartamentos] = useState<Departamento[]>([]);
  const [tiposDocumento, setTiposDocumento] = useState<TipoDocumento[]>([]);
  const [loading, setLoading] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState<FiltrosDocumento>({});
  const [error, setError] = useState<string | null>(null);
  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);
  const [activeSection, setActiveSection] = useState<
    "savedFilters" | "stats" | null
  >(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [filterName, setFilterName] = useState("");

  const [stats, setStats] = useState<Stats | null>(null);
  const [view, setView] = useState<"simple" | "detailed">("simple");
  const [datePickerKey, setDatePickerKey] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isPremiumDialogOpen, setIsPremiumDialogOpen] = useState(false);
  const [isTableExpanded, setIsTableExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  // Efecto para inicializar itemsPerPage desde la URL o localStorage
  useEffect(() => {
    // Intentar obtener el límite de la URL
    const searchParams = new URLSearchParams(window.location.search);
    const urlLimit = searchParams.get("limit");

    // Intentar obtener el límite del localStorage
    const savedLimit = localStorage.getItem("preferredLimit");

    // Priorizar el límite de la URL, luego localStorage, o usar el valor por defecto
    if (urlLimit) {
      setItemsPerPage(Number(urlLimit));
    } else if (savedLimit) {
      setItemsPerPage(Number(savedLimit));
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const [seccionesData, departamentosData, tiposDocumentoData] =
          await Promise.all([
            fetchSecciones(),
            fetchDepartamentos(),
            fetchTiposDocumento(),
          ]);
        setSecciones(seccionesData);
        setDepartamentos(departamentosData);
        setTiposDocumento(tiposDocumentoData);
      } catch (err) {
        console.error("Error fetching filter data:", err);
        setError(
          "Error al cargar los datos de filtros. Inténtelo de nuevo más tarde."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleFilterChange = useCallback(
    (name: keyof FiltrosDocumento, value: string | string[]) => {
      console.log(`Filter change called: ${name} = ${JSON.stringify(value)}`);

      // Handle 'all' value or empty array for multi-select
      if (value === "all" || (Array.isArray(value) && value.length === 0)) {
        setFiltros((prev) => {
          // If already deleted, don't update state again
          if (!(name in prev)) return prev;

          const newFilters = { ...prev };
          delete newFilters[name];
          console.log(`Filtro cambiado: ${name} = all (eliminado)`);
          return newFilters;
        });
      } else {
        // Si es un valor válido, asignarlo
        setFiltros((prev) => {
          // Check if arrays are equal to avoid unnecessary updates
          if (Array.isArray(value) && Array.isArray(prev[name])) {
            if (JSON.stringify(value) === JSON.stringify(prev[name])) {
              return prev;
            }
          } else if (prev[name] === value) {
            return prev;
          }

          const newFilters = { ...prev };
          newFilters[name] = value as string;
          console.log(`Filtro cambiado: ${name} = ${JSON.stringify(value)}`);
          return newFilters;
        });
      }
    },
    []
  );

  const handleTextFilterChange = (value: string) => {
    setFiltros((prev) => ({
      ...prev,
      texto: value || undefined,
    }));
  };

  const handleTextFilterKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      applyFilters(filtros);
    }
  };

  // Get initial date range from filters if they exist
  const getInitialDateRange = () => {
    if (filtros.fecha_desde) {
      const from = new Date(filtros.fecha_desde);

      if (filtros.fecha_hasta) {
        // If both dates exist, return a complete range
        return {
          from,
          to: new Date(filtros.fecha_hasta),
        };
      }

      // If only from date exists
      return { from, to: undefined };
    } else if (filtros.fecha_hasta) {
      // If only to date exists, use it as both from and to
      const to = new Date(filtros.fecha_hasta);
      return { from: to, to };
    }

    return undefined;
  };

  const handleDateRangeChange = (range: { from?: Date; to?: Date }) => {
    const newFilters = { ...filtros };

    if (range.from) {
      newFilters.fecha_desde = format(range.from, "yyyy-MM-dd");
    } else {
      delete newFilters.fecha_desde;
    }

    if (range.to) {
      newFilters.fecha_hasta = format(range.to, "yyyy-MM-dd");
    } else {
      delete newFilters.fecha_hasta;
    }

    setFiltros(newFilters);
  };

  const handleApplyFilters = () => {
    console.log("🔍 Filters - handleApplyFilters: Aplicando filtros", filtros);
    applyFilters(filtros);
  };

  const handleSaveFilters = async () => {
    if (!filterName) {
      alert("Por favor, introduce un nombre para el filtro.");
      return;
    }

    console.log("💾 Filters - handleSaveFilters: Guardando filtro", filterName);

    try {
      const filterData = {
        username: "test", // Cambia esto por el nombre de usuario real
        filter_name: filterName, // Usa el nombre del filtro ingresado
        section_filter: filtros.seccion || undefined,
        department_filter: filtros.departamento || undefined,
        document_type_filter: filtros.tipo_documento || undefined,
      };

      await axios.post("http://localhost:8000/api/user_filters", filterData);
      console.log("✅ Filters - Filtro guardado exitosamente");

      // Aplicar filtros después de guardar
      applyFilters(filtros); // Llama a applyFilters con los filtros actuales

      setIsModalOpen(false); // Cierra el modal después de guardar
      setFilterName(""); // Resetea el nombre del filtro
    } catch (error) {
      console.error("❌ Filters - Error al guardar el filtro:", error);
    }
  };

  const fetchSavedFilters = async () => {
    try {
      const response = await axios.get(
        "http://localhost:8000/api/user_filters",
        {
          params: { username: "test" }, // Cambia esto por el nombre de usuario real
        }
      );
      setSavedFilters(response.data);
    } catch (error) {
      console.error("Error al obtener los filtros guardados:", error);
    }
  };

  const handleResetFilters = () => {
    console.log("🔄 Filters - handleResetFilters: Resetando filtros");

    // Set a completely new empty object to avoid reference issues
    setFiltros({});
    setAppliedFilters({});

    // Enviar filtros vacíos al componente padre
    const emptyFilters: Record<string, string> = {
      limit: String(itemsPerPage),
      offset: "0",
    };
    onApplyFilters(emptyFilters as unknown as ApiFiltrosDocumento);

    // Resetear la página actual
    setCurrentPage(1);

    // Increment the key to force re-render of all components
    setDatePickerKey((prev) => prev + 1);

    // Also clear any date range selection
    if (
      document.querySelector('input[placeholder="Seleccionar rango de fechas"]')
    ) {
      (
        document.querySelector(
          'input[placeholder="Seleccionar rango de fechas"]'
        ) as HTMLInputElement
      ).value = "";
    }

    console.log("✅ Filters - Filtros resetados");
  };

  const deleteFilter = async (filterId: number) => {
    try {
      await axios.delete(`http://localhost:8000/api/user_filters/${filterId}`);
      fetchSavedFilters();
    } catch (error) {
      console.error("Error al eliminar el filtro:", error);
    }
  };

  const getFilterLabel = (key: string, value: string | string[]): string => {
    if (Array.isArray(value)) {
      // For array values (multiple selections)
      switch (key) {
        case "seccion":
          return `Secciones: ${value
            .map((v) => {
              const seccion = secciones.find((s) => s.codigo === v);
              return seccion ? seccion.nombre : v;
            })
            .join(", ")}`;
        case "departamento":
          return `Departamentos: ${value
            .map((v) => {
              const departamento = departamentos.find((d) => d.codigo === v);
              return departamento ? departamento.nombre : v;
            })
            .join(", ")}`;
        case "tipo_documento":
          return `Tipos: ${value
            .map((v) => {
              const tipoDoc = tiposDocumento.find((t) => t.tipo === v);
              return tipoDoc ? tipoDoc.label : v;
            })
            .join(", ")}`;
        default:
          return `${key}: ${value.join(", ")}`;
      }
    } else {
      // For single values (backward compatibility)
      switch (key) {
        case "seccion":
          const seccion = secciones.find((s) => s.codigo === value);
          return seccion ? `Sección: ${seccion.nombre}` : `Sección: ${value}`;
        case "departamento":
          const departamento = departamentos.find((d) => d.codigo === value);
          return departamento
            ? `Departamento: ${departamento.nombre}`
            : `Departamento: ${value}`;
        case "tipo_documento":
          const tipoDoc = tiposDocumento.find((t) => t.tipo === value);
          return tipoDoc ? `Tipo: ${tipoDoc.label}` : `Tipo: ${value}`;
        case "texto":
          return `Texto: ${value}`;
        case "fecha_desde":
          return `Desde: ${value}`;
        case "fecha_hasta":
          return `Hasta: ${value}`;
        default:
          return `${key}: ${value}`;
      }
    }
  };

  const fetchStats = async (selectedDate?: Date) => {
    try {
      // Format the date as YYYY-MM-DD if provided, otherwise use the current date
      const formattedDate = selectedDate
        ? `${selectedDate.getFullYear()}-${String(
            selectedDate.getMonth() + 1
          ).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(
            2,
            "0"
          )}`
        : new Date().toISOString().split("T")[0];

      const response = await axios.get("http://localhost:8000/stats/today", {
        params: { fecha: formattedDate },
      });

      console.log("Respuesta de estadísticas:", response.data);

      // Verificar el formato de la respuesta
      if (
        response.data.total_documentos === 0 ||
        (!response.data.simple && !response.data.secciones)
      ) {
        // Si no hay documentos o la estructura no es la esperada, crear un objeto Stats vacío
        setStats({
          total_count: response.data.total_documentos || 0,
          simple: [],
          detailed: [],
          fecha: response.data.fecha || formattedDate,
        });
      } else if (response.data.simple) {
        // Si la respuesta ya tiene el formato esperado
        setStats(response.data);
      } else if (
        response.data.secciones &&
        response.data.secciones.length > 0
      ) {
        // Si la respuesta es del formato nuevo (secciones, departamentos, tipos_documento)
        // Transformar la respuesta al formato esperado por el componente

        // 1. Crear estadísticas simples a partir de tipos_documento
        const simple: SimpleStatistic[] = response.data.tipos_documento.map(
          (tipo: any) => ({
            tipo_documento: tipo.nombre || tipo.tipo,
            cantidad: tipo.num_documentos,
            porcentaje:
              (tipo.num_documentos / response.data.total_documentos) * 100,
          })
        );

        console.log("Datos simples transformados:", simple);

        // 2. Crear estadísticas detalladas a partir de departamentos
        const detailed: DetailedStatistic[] = [];

        // Por cada departamento, extraer su información
        if (response.data.departamentos) {
          console.log("Procesando departamentos:", response.data.departamentos);

          // Verificar si los departamentos tienen tipos_documento anidados
          const tienenTiposDocumentoAnidados =
            response.data.departamentos.length > 0 &&
            response.data.departamentos[0].tipos_documento &&
            Array.isArray(response.data.departamentos[0].tipos_documento) &&
            response.data.departamentos[0].tipos_documento.length > 0;

          if (tienenTiposDocumentoAnidados) {
            // Si los departamentos tienen tipos_documento anidados, usamos esa información detallada
            response.data.departamentos.forEach((depto: any) => {
              if (
                depto.tipos_documento &&
                Array.isArray(depto.tipos_documento)
              ) {
                depto.tipos_documento.forEach((tipo: any) => {
                  detailed.push({
                    departamento_nombre: depto.nombre,
                    tipo_documento: tipo.nombre || tipo.tipo,
                    cantidad: tipo.num_documentos,
                    porcentaje:
                      (tipo.num_documentos / response.data.total_documentos) *
                      100,
                  });
                });
              }
            });
          } else {
            // Si los departamentos NO tienen tipos_documento anidados, creamos una entrada por departamento
            response.data.departamentos.forEach((depto: any) => {
              detailed.push({
                departamento_nombre: depto.nombre,
                tipo_documento: "", // Usamos "Total" como tipo para indicar que es el total del departamento
                cantidad: depto.num_documentos,
                porcentaje:
                  (depto.num_documentos / response.data.total_documentos) * 100,
              });
            });
          }
        }

        console.log("Datos detallados transformados:", detailed);

        // 3. Crear el objeto Stats
        const statsData = {
          total_count: response.data.total_documentos,
          simple: simple,
          detailed: detailed,
          fecha: response.data.fecha,
          total_documentos: response.data.total_documentos,
          secciones: response.data.secciones,
          departamentos: response.data.departamentos,
          tipos_documento: response.data.tipos_documento,
        };

        console.log("Objeto Stats final:", statsData);
        setStats(statsData);
      } else {
        // Si la estructura no es reconocida
        console.error(
          "Formato de respuesta de estadísticas no reconocido:",
          response.data
        );
        setStats({
          total_count: response.data.total_documentos || 0,
          simple: [],
          detailed: [],
          fecha: response.data.fecha || formattedDate,
        });
      }

      setIsTableExpanded(false); // Reset expanded state when new stats are loaded
      setActiveSection("stats");
    } catch (error) {
      console.error("Error fetching statistics:", error);
      // En caso de error, establecer stats como un objeto vacío pero válido
      setStats({
        total_count: 0,
        simple: [],
        detailed: [],
      });
    }
  };

  // Prepare data for the donut chart - asegurarse de que simple existe antes de hacer map
  const donutData = {
    labels: stats?.simple?.map((item) => item.tipo_documento) || [],
    datasets: [
      {
        data: stats?.simple?.map((item) => item.cantidad) || [],
        backgroundColor: [
          "#3b82f6", // blue-500
          "#ef4444", // red-500
          "#10b981", // emerald-500
          "#f59e0b", // amber-500
          "#8b5cf6", // violet-500
          "#6366f1", // indigo-500
          "#ec4899", // pink-500
          "#14b8a6", // teal-500
          "#f97316", // orange-500
          "#a855f7", // purple-500
        ],
        borderWidth: 1,
        borderColor: "#0f172a", // slate-900 - dark theme compatible
      },
    ],
  };

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "right" as const,
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
          font: {
            size: 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || "";
            const value = context.raw || 0;
            const total = context.chart.getDatasetMeta(0).total;
            const percentage = Math.round((value / total) * 100);
            return `${label}: ${value} (${percentage}%)`;
          },
        },
      },
    },
    cutout: "65%",
  };

  // Reset active state when search is performed
  const handleSearch = () => {
    // Use your existing search function instead of calling handleSearch again
    // For example: applyFilters(filtros);

    // Reset active section
    setActiveSection(null);
  };

  // Modify your existing toggle functions to update the active state

  // For the Filtros Guardados button
  const handleToggleSavedFilters = () => {
    const newActiveSection =
      activeSection === "savedFilters" ? null : "savedFilters";
    setActiveSection(newActiveSection);
    if (newActiveSection === "savedFilters") {
      fetchSavedFilters();
    }
  };

  // For the Estadísticas del día button
  const handleToggleStats = () => {
    const newActiveSection = activeSection === "stats" ? null : "stats";
    setActiveSection(newActiveSection);
    if (newActiveSection === "stats") {
      fetchStats(); // This will use the current date as default
    }
  };

  const handlePremiumSuccess = () => {
    // Mostrar los filtros después de suscribirse
    setActiveSection(null);
  };

  // For the Toggle component
  const handleViewChange = (newView: "simple" | "detailed") => {
    setView(newView);
    if (newView === "detailed") {
      setIsTableExpanded(false); // Reset expanded state when switching to detailed view
    }
  };

  // Crear una función adaptadora para manejar la incompatibilidad de tipos
  const handleSetDate = (date: Date | null) => {
    setSelectedDate(date || undefined);
  };

  // const filteredDepartamentos = useMemo(() => {
  //   if (!departamentoSearch.trim()) return departamentos;

  //   return departamentos.filter((dep) =>
  //     dep.nombre.toLowerCase().includes(departamentoSearch.toLowerCase())
  //   );
  // }, [departamentos, departamentoSearch]);

  // Helper to convert potentially string or string[] to string[]
  const getSelectedValues = (key: keyof FiltrosDocumento): string[] => {
    const value = filtros[key];
    if (Array.isArray(value)) {
      return value;
    } else if (value) {
      return [value];
    }
    return [];
  };

  const applyFilters = async (filtersToApply: FiltrosDocumento) => {
    console.log(
      "🔍 Filters - applyFilters: Procesando filtros",
      filtersToApply
    );

    // Crear una versión API-compatible de los filtros (todo debe ser string)
    const apiFilters: Record<string, string> = {};

    // Convertir arrays a strings separados por comas
    Object.entries(filtersToApply).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          apiFilters[key] = value.join(",");
        }
      }
      // Valores string normales
      else if (value !== undefined && value !== "" && value !== "undefined") {
        apiFilters[key] = String(value);
      }
    });

    // Asegurarse de mantener los parámetros de paginación actuales
    apiFilters.limit = String(itemsPerPage);
    apiFilters.offset = String((currentPage - 1) * itemsPerPage);

    // Guardar filtros y aplicarlos
    setAppliedFilters(filtersToApply);

    console.log(
      "🔄 Filters - applyFilters: Enviando filtros al componente padre",
      apiFilters
    );
    onApplyFilters(apiFilters as unknown as ApiFiltrosDocumento);

    // Eliminamos la llamada a fetchDocumentos para evitar llamadas duplicadas
    console.log("✅ Filters - Filtros aplicados");
  };

  return (
    <div className="mb-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="col-span-1 md:col-span-2">
          <div className="card-container p-4">
            <div className="flex flex-col space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium">Filtros de búsqueda</h2>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResetFilters}
                    className="filter-button"
                  >
                    <Eraser className="h-4 w-4 mr-2" />
                    Limpiar filtros
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleToggleSavedFilters}
                    className="filter-button"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Filtros guardados
                  </Button>
                  <PremiumButton
                    onClick={handleToggleStats}
                    icon={<BarChart2 className="h-4 w-4 mr-2" />}
                  >
                    Estadísticas
                  </PremiumButton>
                </div>
              </div>

              {/* Row 1: Sección and Departamento */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Sección</label>
                  <MultiSelect
                    key={`seccion-multiselect-${datePickerKey}`}
                    options={secciones.map((seccion) => ({
                      value: seccion.codigo,
                      label: seccion.nombre,
                    }))}
                    selected={getSelectedValues("seccion")}
                    onChange={(values) => handleFilterChange("seccion", values)}
                    placeholder="Todas las secciones"
                    allOptionLabel="Todas las secciones"
                    className="search-input w-full"
                    style={{ minHeight: "40px" }}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Departamento</label>
                  <MultiSelect
                    key={`departamento-multiselect-${datePickerKey}`}
                    options={departamentos.map((departamento) => ({
                      value: departamento.codigo,
                      label: departamento.nombre,
                    }))}
                    selected={getSelectedValues("departamento")}
                    onChange={(values) =>
                      handleFilterChange("departamento", values)
                    }
                    placeholder="Todos los departamentos"
                    allOptionLabel="Todos los departamentos"
                    showSearch={true}
                    searchThreshold={5}
                    className="search-input w-full"
                    style={{ minHeight: "40px" }}
                  />
                </div>
              </div>

              {/* Row 2: Tipo de documento and Rango de fechas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    Tipo de documento
                  </label>
                  <MultiSelect
                    key={`tipo-documento-multiselect-${datePickerKey}`}
                    options={tiposDocumento.map((tipo) => ({
                      value: tipo.tipo,
                      label: tipo.label,
                    }))}
                    selected={getSelectedValues("tipo_documento")}
                    onChange={(values) =>
                      handleFilterChange("tipo_documento", values)
                    }
                    placeholder="Todos los tipos"
                    allOptionLabel="Todos los tipos"
                    className="search-input w-full"
                    style={{ minHeight: "40px" }}
                    showSearch={false}
                    searchThreshold={9999}
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-sm font-medium">Rango de fechas</label>
                  <DatePickerWithRange
                    initialDateRange={getInitialDateRange()}
                    onDateRangeChange={handleDateRangeChange}
                    className="search-input w-full"
                  />
                </div>
              </div>

              {/* Row 3: Texto a buscar */}
              <div className="space-y-2 mt-4">
                <label className="text-sm font-medium">Texto a buscar</label>
                <div className="relative">
                  <Input
                    type="text"
                    placeholder="Buscar en títulos..."
                    value={filtros.texto || ""}
                    onChange={(e) => handleTextFilterChange(e.target.value)}
                    onKeyDown={handleTextFilterKeyDown}
                    className="search-input pr-10"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-0 top-0 h-full"
                    onClick={handleSearch}
                  >
                    <Search className="h-4 w-4 text-primary" />
                  </Button>
                </div>
              </div>

              <div className="flex justify-end space-x-2">
                <PremiumButton
                  variant="outline"
                  onClick={() => setIsModalOpen(true)}
                  className="filter-button"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Guardar filtros
                </PremiumButton>
                <Button
                  onClick={handleApplyFilters}
                  className="w-full md:w-auto"
                >
                  <SearchCheck className="h-4 w-4 mr-2" />
                  Aplicar filtros
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="col-span-1">
          <div className="card-container p-4 h-full">
            <div className="flex flex-col h-full">
              <h2 className="text-lg font-medium mb-4">Filtros aplicados</h2>
              {Object.entries(filtros).some(
                ([key, value]) =>
                  value && key !== "page" && key !== "itemsPerPage"
              ) ? (
                <div
                  className="space-y-2"
                  key={`applied-filters-${Object.keys(filtros).join("-")}`}
                >
                  {/* Filtros de sección, departamento y tipo de documento */}
                  {Object.entries(filtros).map(([key, value]) => {
                    if (
                      !value ||
                      key === "page" ||
                      key === "itemsPerPage" ||
                      key === "fecha_desde" ||
                      key === "fecha_hasta" ||
                      key === "texto"
                    )
                      return null;
                    return (
                      <div
                        key={`${key}-${value}`}
                        className="flex items-center justify-between bg-secondary/30 p-2 rounded-md"
                      >
                        <div>
                          <span className="text-sm font-medium">
                            {getFilterLabel(key, value)}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => {
                            // Ensure key is a valid filter property
                            if (
                              key === "seccion" ||
                              key === "departamento" ||
                              key === "tipo_documento" ||
                              key === "texto" ||
                              key === "fecha_desde" ||
                              key === "fecha_hasta" ||
                              key === "limit" ||
                              key === "offset"
                            ) {
                              handleFilterChange(key, "all");
                            }
                          }}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    );
                  })}

                  {/* Filtro de rango de fechas (mostrar solo uno combinado) */}
                  {(filtros.fecha_desde || filtros.fecha_hasta) && (
                    <div className="flex items-center justify-between bg-secondary/30 p-2 rounded-md">
                      <div>
                        <span className="text-sm font-medium">
                          {filtros.fecha_desde && filtros.fecha_hasta
                            ? `Desde: ${format(
                                new Date(filtros.fecha_desde),
                                "dd/MM/yyyy"
                              )} - Hasta: ${format(
                                new Date(filtros.fecha_hasta),
                                "dd/MM/yyyy"
                              )}`
                            : filtros.fecha_desde
                            ? `Desde: ${format(
                                new Date(filtros.fecha_desde),
                                "dd/MM/yyyy"
                              )}`
                            : filtros.fecha_hasta
                            ? `Hasta: ${format(
                                new Date(filtros.fecha_hasta),
                                "dd/MM/yyyy"
                              )}`
                            : ""}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => {
                          setFiltros((prev) => {
                            const newFilters = { ...prev };
                            delete newFilters.fecha_desde;
                            delete newFilters.fecha_hasta;
                            return newFilters;
                          });
                          // Forzar actualización del DatePickerWithRange
                          setDatePickerKey((prev) => prev + 1);
                        }}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}

                  {/* Filtro de texto */}
                  {filtros.texto && (
                    <div className="flex items-center justify-between bg-secondary/30 p-2 rounded-md">
                      <div>
                        <span className="text-sm font-medium">
                          Texto: {filtros.texto}
                        </span>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => handleTextFilterChange("")}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center flex-grow text-center text-muted-foreground">
                  <Filter className="h-12 w-12 mb-2 opacity-50" />
                  <p>No hay filtros aplicados</p>
                  <p className="text-sm">
                    Utiliza los controles de la izquierda para filtrar los
                    resultados
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-destructive/10 text-destructive p-4 rounded-md">
          {error}
        </div>
      )}

      {/* Sección de filtros guardados */}
      {activeSection === "savedFilters" && (
        <div className="mt-6 card-container p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Filtros guardados</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={() => fetchSavedFilters()}
              className="filter-button"
            >
              <RefreshCwOff className="h-4 w-4 mr-2" />
              Actualizar
            </Button>
          </div>

          {savedFilters.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
              <Save className="h-12 w-12 mb-2 opacity-50" />
              <p>No hay filtros guardados</p>
              <p className="text-sm">
                Guarda tus filtros favoritos para acceder rápidamente a ellos
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {savedFilters.map((filter) => (
                <div key={filter.id} className="card-container p-4 card-hover">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-medium">{filter.filter_name}</h3>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6 text-destructive"
                      onClick={() => deleteFilter(filter.id)}
                    >
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>

                  <div className="text-sm text-muted-foreground mb-4">
                    {filter.section_filter && (
                      <div className="mb-1">
                        <span className="font-medium">Sección:</span>{" "}
                        {filter.section_filter}
                      </div>
                    )}
                    {filter.department_filter && (
                      <div className="mb-1">
                        <span className="font-medium">Departamento:</span>{" "}
                        {filter.department_filter}
                      </div>
                    )}
                    {filter.document_type_filter && (
                      <div className="mb-1">
                        <span className="font-medium">Tipo:</span>{" "}
                        {filter.document_type_filter}
                      </div>
                    )}
                  </div>

                  <Button
                    onClick={() => {
                      const filtersToApply = {
                        seccion: filter.section_filter || undefined,
                        departamento: filter.department_filter || undefined,
                        tipo_documento:
                          filter.document_type_filter || undefined,
                      };
                      setFiltros(filtersToApply);
                      applyFilters(filtersToApply);
                      setActiveSection(null);
                    }}
                    className="w-full"
                  >
                    <SearchCheck className="h-4 w-4 mr-2" />
                    Aplicar filtro
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Sección de estadísticas */}
      {activeSection === "stats" && (
        <div className="mt-6 card-container p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium">Estadísticas del día</h2>
            <div className="flex items-center space-x-2">
              <DatePicker
                date={selectedDate}
                setDate={handleSetDate}
                placeholder="Seleccionar fecha"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchStats(selectedDate)}
                className="filter-button"
              >
                <RefreshCwOff className="h-4 w-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>

          {!stats ? (
            <div className="flex flex-col items-center justify-center py-8 text-center text-muted-foreground">
              <BarChart2 className="h-12 w-12 mb-2 opacity-50" />
              <p>No hay estadísticas disponibles</p>
              <p className="text-sm">
                Selecciona una fecha para ver las estadísticas
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-md font-medium">
                    Total de documentos:{" "}
                    {stats.total_count || stats.total_documentos || 0}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Fecha:{" "}
                    {stats.fecha
                      ? format(new Date(stats.fecha), "dd/MM/yyyy")
                      : selectedDate
                      ? format(selectedDate, "dd/MM/yyyy")
                      : format(new Date(), "dd/MM/yyyy")}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant={view === "simple" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleViewChange("simple")}
                    className="text-xs"
                  >
                    Vista simple
                  </Button>
                  <Button
                    variant={view === "detailed" ? "default" : "outline"}
                    size="sm"
                    onClick={() => handleViewChange("detailed")}
                    className="text-xs"
                  >
                    Vista detallada
                  </Button>
                </div>
              </div>

              {view === "simple" && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="h-[300px] flex items-center justify-center bg-secondary/10 p-4 rounded-lg">
                    {stats?.simple?.length > 0 ? (
                      <Doughnut data={donutData} options={donutOptions} />
                    ) : (
                      <div className="flex flex-col items-center justify-center text-center">
                        <BarChart2 className="h-12 w-12 mb-2 opacity-50" />
                        <p className="text-muted-foreground">
                          No hay datos para mostrar en esta fecha
                        </p>
                      </div>
                    )}
                  </div>
                  <div>
                    <h4 className="text-sm font-medium mb-2 px-1">
                      Distribución por tipo de documento
                    </h4>
                    {stats?.simple?.length > 0 ? (
                      <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1 custom-scrollbar">
                        {stats.simple.map((item, index) => (
                          <div
                            key={index}
                            className="flex justify-between items-center p-3 bg-secondary/20 hover:bg-secondary/30 transition-colors rounded-md"
                          >
                            <div className="flex items-center">
                              <div
                                className="w-3 h-3 rounded-full mr-2"
                                style={{
                                  backgroundColor:
                                    donutData.datasets[0].backgroundColor[
                                      index %
                                        donutData.datasets[0].backgroundColor
                                          .length
                                    ],
                                }}
                              ></div>
                              <span className="font-medium">
                                {item.tipo_documento}
                              </span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="text-sm font-bold">
                                {item.cantidad}
                              </span>
                              <span className="text-xs bg-secondary/30 px-2 py-1 rounded-full">
                                {item.porcentaje.toFixed(1)}%
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-4 bg-secondary/30 rounded-md text-center">
                        <p>No hay datos de documentos para esta fecha</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {view === "detailed" && (
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className="text-sm font-medium">
                      Distribución detallada por departamento y tipo
                    </h4>
                  </div>
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader className="bg-secondary/30 sticky top-0">
                        <TableRow>
                          <TableHead className="w-[40%] font-semibold">
                            Departamento
                          </TableHead>
                          <TableHead className="w-[25%] font-semibold">
                            Tipo
                          </TableHead>
                          <TableHead className="w-[15%] text-right font-semibold">
                            Cantidad
                          </TableHead>
                          <TableHead className="w-[15%] text-right font-semibold">
                            Porcentaje
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {stats?.detailed?.length > 0 ? (
                          stats.detailed
                            .slice(0, isTableExpanded ? undefined : 10)
                            .map((item, index) => (
                              <TableRow
                                key={index}
                                className="hover:bg-secondary/20 transition-colors"
                              >
                                <TableCell className="font-medium">
                                  {item.departamento_nombre}
                                </TableCell>
                                <TableCell>
                                  {item.tipo_documento ? (
                                    <span className="px-2 py-1 bg-secondary/20 rounded-full text-xs">
                                      {item.tipo_documento}
                                    </span>
                                  ) : (
                                    <span className="text-muted-foreground">
                                      —
                                    </span>
                                  )}
                                </TableCell>
                                <TableCell className="text-right font-semibold">
                                  {item.cantidad}
                                </TableCell>
                                <TableCell className="text-right">
                                  <span className="bg-secondary/30 px-2 py-1 rounded-full text-xs">
                                    {item.porcentaje.toFixed(1)}%
                                  </span>
                                </TableCell>
                              </TableRow>
                            ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-8">
                              <div className="flex flex-col items-center justify-center">
                                <BarChart2 className="h-8 w-8 mb-2 opacity-50" />
                                <p>
                                  No hay datos de documentos para esta fecha
                                </p>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                  <div className="text-center mt-4">
                    {stats?.detailed?.length > 10 && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setIsTableExpanded(!isTableExpanded)}
                        className="text-xs"
                      >
                        {isTableExpanded
                          ? "Mostrar menos"
                          : `Mostrar todos los ${stats.detailed.length} resultados`}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <Modal open={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <div className="p-6">
          <h2 className="text-xl font-semibold mb-4">Guardar filtros</h2>
          <p className="text-muted-foreground mb-4">
            Guarda esta configuración de filtros para usarla más tarde.
          </p>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium block mb-2">
                Nombre del filtro
              </label>
              <Input
                type="text"
                value={filterName}
                onChange={(e) => setFilterName(e.target.value)}
                placeholder="Ej: Decretos de Hacienda"
                className="search-input w-full"
              />
            </div>

            <div className="bg-secondary/20 p-3 rounded-md">
              <h3 className="text-sm font-medium mb-2">
                Filtros que se guardarán:
              </h3>
              <ul className="space-y-1 text-sm">
                {filtros.seccion && (
                  <li>
                    • Sección: {getFilterLabel("seccion", filtros.seccion)}
                  </li>
                )}
                {filtros.departamento && (
                  <li>
                    • Departamento:{" "}
                    {getFilterLabel("departamento", filtros.departamento)}
                  </li>
                )}
                {filtros.tipo_documento && (
                  <li>
                    • Tipo:{" "}
                    {getFilterLabel("tipo_documento", filtros.tipo_documento)}
                  </li>
                )}
                {(filtros.fecha_desde || filtros.fecha_hasta) && (
                  <li>
                    • Fechas:{" "}
                    {filtros.fecha_desde
                      ? `Desde ${format(
                          new Date(filtros.fecha_desde),
                          "dd/MM/yyyy"
                        )}`
                      : ""}
                    {filtros.fecha_hasta
                      ? `${filtros.fecha_desde ? " - " : ""}Hasta ${format(
                          new Date(filtros.fecha_hasta),
                          "dd/MM/yyyy"
                        )}`
                      : ""}
                  </li>
                )}
                {filtros.texto && <li>• Texto: {filtros.texto}</li>}
              </ul>
            </div>
          </div>

          <div className="flex justify-end mt-6 space-x-2">
            <Button onClick={() => setIsModalOpen(false)} variant="outline">
              Cancelar
            </Button>
            <Button
              onClick={handleSaveFilters}
              disabled={!filterName.trim()}
              className="btn-primary"
            >
              <Save className="h-4 w-4 mr-2" />
              Guardar
            </Button>
          </div>
        </div>
      </Modal>

      <PremiumDialog
        isOpen={isPremiumDialogOpen}
        onOpenChange={setIsPremiumDialogOpen}
        onSuccess={handlePremiumSuccess}
      />
    </div>
  );
};

export default Filters;
